import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IconRefresh, IconX } from '@tabler/icons-react';
import type { BoardStage } from '../../../entities/board';
import { partnerPb } from '../../../shared/api/partnerPocketbase';
import { Button } from '../../../shared/ui/Button';
import { Select, type FetchOptionsResult, type SelectOption } from '../../../shared/ui/Select';

const PERIOD_OPTIONS: SelectOption[] = [
  { value: 'today',     label: 'Сегодня' },
  { value: 'yesterday', label: 'Вчера' },
  { value: '7d',        label: '7 дней' },
  { value: '30d',       label: '30 дней' },
  { value: 'all',       label: 'Всё время' },
  { value: 'custom',    label: 'Произвольный период' },
];

type AnalyticsPeriod = 'today' | 'yesterday' | '7d' | '30d' | 'all' | 'custom';

interface AnalyticsStageStat {
  stageId: string;
  stageName: string;
  count: number;
  colors?: BoardStage['colors'];
  finishSuccess?: boolean;
  finishClose?: boolean;
}

interface DailyRow {
  date: string;
  counts: Record<string, number>;
  total: number;
}

const API_BASE = import.meta.env.DEV ? 'http://127.0.0.1:8090' : '';

const fetchPartnerOptions = async ({ page, search }: { page: number; search: string }): Promise<FetchOptionsResult> => {
  const perPage = 20;
  const safeSearch = search.replace(/"/g, '');
  const filter = safeSearch ? `source ~ "${safeSearch}"` : '';
  const result = await partnerPb.collection('unique_sources_parnters').getList<{
    id: string;
    source: string;
  }>(page, perPage, {
    sort: 'source',
    filter: filter || undefined,
    fields: 'id,source',
    requestKey: null,
  });
  const seen = new Set<string>();
  const items = result.items
    .filter(r => {
      if (!r.source || seen.has(r.source)) return false;
      seen.add(r.source);
      return true;
    })
    .map(r => ({ value: r.source, label: r.source }));
  return { items, hasMore: page * perPage < result.totalItems };
};

const FILTERS_KEY = 'partner_analytics_filters';

const readSavedFilters = (): Record<string, unknown> => {
  try { return JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}'); } catch { return {}; }
};

const DEFAULT_FILTERS = {
  period: '30d' as AnalyticsPeriod,
  customStart: '',
  customEnd: '',
  stageIds: [] as string[],
  partnerIds: [] as string[],
};

const PartnerAnalyticsSection: React.FC = () => {
  const [analyticsPeriod, setAnalyticsPeriod] = useState<AnalyticsPeriod>(() => (readSavedFilters().period as AnalyticsPeriod) ?? DEFAULT_FILTERS.period);
  const [analyticsCustomStart, setAnalyticsCustomStart] = useState(() => (readSavedFilters().customStart as string) ?? DEFAULT_FILTERS.customStart);
  const [analyticsCustomEnd, setAnalyticsCustomEnd] = useState(() => (readSavedFilters().customEnd as string) ?? DEFAULT_FILTERS.customEnd);
  const [analyticsPartnerIds, setAnalyticsPartnerIds] = useState<string[]>(() => (readSavedFilters().partnerIds as string[]) ?? DEFAULT_FILTERS.partnerIds);

  const [stages, setStages] = useState<BoardStage[]>([]);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsStageStat[]>([]);
  const [dailyData, setDailyData] = useState<DailyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedStageIds, setSelectedStageIds] = useState<string[]>(() => (readSavedFilters().stageIds as string[]) ?? DEFAULT_FILTERS.stageIds);


  useEffect(() => {
    localStorage.setItem(FILTERS_KEY, JSON.stringify({
      period: analyticsPeriod,
      customStart: analyticsCustomStart,
      customEnd: analyticsCustomEnd,
      stageIds: selectedStageIds,
      partnerIds: analyticsPartnerIds,
    }));
  }, [analyticsPeriod, analyticsCustomStart, analyticsCustomEnd, selectedStageIds, analyticsPartnerIds]);

  const resetFilters = () => {
    localStorage.removeItem(FILTERS_KEY);
    setAnalyticsPeriod(DEFAULT_FILTERS.period);
    setAnalyticsCustomStart(DEFAULT_FILTERS.customStart);
    setAnalyticsCustomEnd(DEFAULT_FILTERS.customEnd);
    setSelectedStageIds(DEFAULT_FILTERS.stageIds);
    setAnalyticsPartnerIds(DEFAULT_FILTERS.partnerIds);
  };

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const activeStages = await partnerPb.collection('board').getFullList<BoardStage>({
        sort: 'order', filter: 'actual=true', fields: 'id,name,order,actual,colors,finishSuccess,finishClose',
      });
      setStages(activeStages);

      let startDate: string;
      let endDate = dayjs().format('YYYY-MM-DD');

      if (analyticsPeriod === 'today') {
        startDate = dayjs().format('YYYY-MM-DD');
      } else if (analyticsPeriod === 'yesterday') {
        startDate = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
        endDate = startDate;
      } else if (analyticsPeriod === '7d') {
        startDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
      } else if (analyticsPeriod === '30d') {
        startDate = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
      } else if (analyticsPeriod === 'custom') {
        startDate = analyticsCustomStart || '2000-01-01';
        endDate = analyticsCustomEnd || dayjs().format('YYYY-MM-DD');
      } else {
        startDate = '2000-01-01';
      }

      const params = new URLSearchParams({ startDate, endDate });
      if (analyticsPartnerIds.length > 0) params.set('source', analyticsPartnerIds.join(','));

      const authHeader = { Authorization: partnerPb.authStore.token ? `Bearer ${partnerPb.authStore.token}` : '' };

      const [statRes, dailyRes] = await Promise.all([
        fetch(`${API_BASE}/api/partner/funnel-stats?${params}`, { headers: authHeader }),
        fetch(`${API_BASE}/api/partner/funnel-stats/daily?${params}`, { headers: authHeader }),
      ]);

      const statData: Record<string, number> = await statRes.json();
      const dailyRaw: Record<string, Record<string, number>> = await dailyRes.json();

      setAnalyticsData(activeStages.map(s => ({
        stageId: s.id,
        stageName: s.name,
        count: statData[s.id] || 0,
        colors: s.colors,
        finishSuccess: s.finishSuccess,
        finishClose: s.finishClose,
      })));

      setDailyData(
        Object.entries(dailyRaw)
          .map(([date, counts]) => ({
            date,
            counts,
            total: Object.values(counts).reduce((s, n) => s + n, 0),
          }))
          .filter(r => r.total > 0)
          .sort((a, b) => b.date.localeCompare(a.date)),
      );
    } catch {
      setAnalyticsData([]);
      setDailyData([]);
    } finally {
      setLoading(false);
    }
  }, [analyticsPeriod, analyticsCustomStart, analyticsCustomEnd, analyticsPartnerIds]);

  useEffect(() => {
    void loadAnalytics();
  }, [loadAnalytics]);

  const visibleStages = useMemo(
    () => selectedStageIds.length === 0 ? stages : stages.filter(s => selectedStageIds.includes(s.id)),
    [stages, selectedStageIds],
  );

  const visibleAnalyticsData = useMemo(
    () => selectedStageIds.length === 0 ? analyticsData : analyticsData.filter(d => selectedStageIds.includes(d.stageId)),
    [analyticsData, selectedStageIds],
  );

  const funnelData = useMemo(
    () => analyticsData.filter(d => !d.finishClose && (selectedStageIds.length === 0 || selectedStageIds.includes(d.stageId))),
    [analyticsData, selectedStageIds],
  );

  const conversionMatrix = useMemo(() => {
    if (funnelData.length < 2) return null;
    return {
      rows: funnelData.slice(0, -1),
      cols: funnelData.slice(1),
    };
  }, [funnelData]);

  const firstCount = funnelData[0]?.count || 1;
  const finishSuccessStat = analyticsData.find(d => d.finishSuccess);
  const finishCloseStat = analyticsData.find(d => d.finishClose);
  const finishSuccessConv = finishSuccessStat && firstCount > 0
    ? (finishSuccessStat.count / firstCount) * 100
    : null;
  const finishCloseConv = finishCloseStat && firstCount > 0
    ? (finishCloseStat.count / firstCount) * 100
    : null;

  return (
    <div className="adm-section">
      <div className="adm-section-header">
        <h2 className="adm-section-title">Воронка продаж</h2>
        <p className="adm-section-sub">Конверсии по этапам</p>
      </div>

      <div className="adm-analytics-filters">
        <div className="adm-analytics-period">
          <Select
            options={PERIOD_OPTIONS}
            value={analyticsPeriod}
            onChange={v => setAnalyticsPeriod(v as AnalyticsPeriod)}
            placeholder="Период"
          />
        </div>
        {analyticsPeriod === 'custom' && (
          <div className="adm-date-range">
            <input type="date" className="adm-date-input" value={analyticsCustomStart}
              onChange={e => setAnalyticsCustomStart(e.target.value)} />
            <span className="adm-date-sep">—</span>
            <input type="date" className="adm-date-input" value={analyticsCustomEnd}
              onChange={e => setAnalyticsCustomEnd(e.target.value)} />
          </div>
        )}
        <div className="adm-analytics-source">
          <Select
            mode="multi"
            fetchOptions={fetchPartnerOptions}
            value={analyticsPartnerIds}
            onChange={setAnalyticsPartnerIds}
            placeholder="Все источники"
          />
        </div>
        {stages.length > 0 && (
          <div className="adm-analytics-stages">
            <Select
              mode="multi"
              options={stages.map(s => ({ value: s.id, label: s.name }))}
              value={selectedStageIds}
              onChange={setSelectedStageIds}
              placeholder="Все колонки"
              searchable
            />
          </div>
        )}
        <Button variant="adm-add" type="button" onClick={() => { void loadAnalytics(); }}>
          <IconRefresh size={14} stroke={2} />Обновить
        </Button>
        <Button variant="adm-cancel" type="button" onClick={resetFilters}>
          <IconX size={14} stroke={2} />Сбросить
        </Button>
      </div>

      {loading ? (
        <div className="adm-loading">Загрузка...</div>
      ) : analyticsData.length === 0 ? (
        <div className="adm-empty">Нет данных</div>
      ) : (
        <div className="af-blocks">
          <div className="af-layout">
            <div className="af-funnel-card">
              <div className="af-funnel-card-title">Воронка</div>
              <div className="af-funnel">
                {funnelData.map((row, i) => {
                  const widthPct = firstCount > 0 ? Math.min(Math.max((row.count / firstCount) * 100, 8), 100) : 8;
                  const prevCount = i > 0 ? funnelData[i - 1].count : row.count;
                  const convPrev = i > 0 ? (prevCount > 0 ? (row.count / prevCount) * 100 : 0) : null;
                  const totalPct = i === 0 ? 100 : (firstCount > 0 ? (row.count / firstCount) * 100 : 0);
                  const c = row.colors;
                  const bg = c?.dotBg ?? 'var(--orange)';
                  const headerBg = c?.headerBg ?? 'var(--orange-light)';
                  const nameColor = c?.nameColor ?? 'var(--orange-dark)';
                  const connCls = convPrev !== null ? (convPrev < 50 ? ' low' : convPrev < 80 ? ' med' : '') : '';

                  return (
                    <React.Fragment key={row.stageId}>
                      {convPrev !== null && (
                        <div className={`af-funnel-arrow${connCls}`}>
                          <span className="af-funnel-arrow-icon">↓</span>
                          <span className="af-funnel-arrow-badge">{convPrev?.toFixed(1)}%</span>
                        </div>
                      )}
                      <div className="af-funnel-row">
                        <div className="af-funnel-label">
                          <span className="af-dot" style={{ background: bg }} />
                          <span className="af-name" style={{ color: nameColor }}>{row.stageName}</span>
                        </div>
                        <div className="af-funnel-bar-area">
                          <div
                            className="af-funnel-bar"
                            style={{ width: `${widthPct}%`, background: headerBg, borderColor: bg + '40' } as React.CSSProperties}
                          >
                            <span className="af-count" style={{ color: nameColor }}>{row.count.toLocaleString()}</span>
                          </div>
                          <span className="af-funnel-pct">{totalPct.toFixed(1)}%</span>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            <div className="af-stats">
              {(finishSuccessConv !== null || finishCloseConv !== null) && (
                <div className="af-stat-card af-conv-card">
                  {finishSuccessConv !== null && finishSuccessStat && (
                    <div className="af-conv-block">
                      <div className="af-conv-header">
                        <span className="af-conv-label">Конверсия в успех</span>
                        <span className={`af-conv-badge${finishSuccessConv < 20 ? ' low' : finishSuccessConv < 50 ? ' med' : ' good'}`}>
                          {finishSuccessConv.toFixed(1)}%
                        </span>
                      </div>
                      <div className="af-conv-track">
                        <div
                          className={`af-conv-fill${finishSuccessConv < 20 ? ' low' : finishSuccessConv < 50 ? ' med' : ' good'}`}
                          style={{ width: `${finishSuccessConv}%` }}
                        />
                      </div>
                      <div className="af-conv-sub">{funnelData[0]?.stageName} → {finishSuccessStat.stageName}</div>
                    </div>
                  )}
                  {finishCloseConv !== null && finishCloseStat && (
                    <>
                      {finishSuccessConv !== null && <div className="af-conv-divider" />}
                      <div className="af-conv-block">
                        <div className="af-conv-header">
                          <span className="af-conv-label">Закрытые без сделки</span>
                          <span className="af-conv-badge low">{finishCloseConv.toFixed(1)}%</span>
                        </div>
                        <div className="af-conv-track">
                          <div className="af-conv-fill low" style={{ width: `${finishCloseConv}%` }} />
                        </div>
                        <div className="af-conv-sub">{finishCloseStat.stageName}: {finishCloseStat.count.toLocaleString()}</div>
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="af-stat-card af-stat-stages">
                <div className="af-stat-label">По этапам</div>
                {visibleAnalyticsData.map((row, i) => {
                  const pct = firstCount > 0 ? (row.count / firstCount) * 100 : 0;
                  const fillPct = i === 0 ? 100 : pct;
                  const c = row.colors;
                  const barColor = c?.dotBg ?? 'var(--orange)';
                  return (
                    <div key={row.stageId} className="af-stat-stage-row">
                      <div className="af-stat-stage-top">
                        <span className="af-stat-stage-dot" style={{ background: barColor }} />
                        <span className="af-stat-stage-name">{row.stageName}</span>
                        <span className="af-stat-stage-count">{row.count.toLocaleString()}</span>
                        <span className="af-stat-stage-pct">{i === 0 ? '100.00%' : `${pct.toFixed(1)}%`}</span>
                      </div>
                      <div className="af-stat-stage-track">
                        <div className="af-stat-stage-fill" style={{ width: `${fillPct}%`, background: barColor }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {conversionMatrix && (
            <div className="af-daily">
              <div className="af-daily-title">Матрица конверсий</div>
              <div className="af-daily-wrap">
                <table className="af-daily-table af-matrix-table">
                  <thead>
                    <tr>
                      <th className="af-th af-th-date" style={{ textAlign: 'left' }}>Этап \ Цель</th>
                      {conversionMatrix.cols.map(col => (
                        <th key={col.stageId} className="af-th">{col.stageName}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {conversionMatrix.rows.map((rowStage, rowIdx) => (
                      <tr key={rowStage.stageId} className="af-tr">
                        <td className="af-td af-td-date">
                          {rowStage.stageName}
                          <span className="af-matrix-base">{rowStage.count.toLocaleString()}</span>
                        </td>
                        {conversionMatrix.cols.map((colStage, colIdx) => {
                          if (colIdx < rowIdx) {
                            return <td key={colStage.stageId} className="af-td af-matrix-empty" />;
                          }
                          const pct = rowStage.count > 0 ? (colStage.count / rowStage.count) * 100 : 0;
                          const isDirect = colIdx === rowIdx;
                          return (
                            <td key={colStage.stageId} className={`af-td af-matrix-cell${isDirect ? ' direct' : ''}`}>
                              {pct.toFixed(1)}%
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {dailyData.length > 0 && (
            <div className="af-daily">
              <div className="af-daily-title">По дням</div>
              <div className="af-daily-wrap">
                <table className="af-daily-table">
                  <thead>
                    <tr>
                      <th className="af-th af-th-date">Дата</th>
                      {visibleStages.map(s => (
                        <th key={s.id} className="af-th">{s.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="af-tr af-tr-total">
                      <td className="af-td af-td-date af-td-total-label">Всего</td>
                      {visibleStages.map(s => {
                        const colTotal = dailyData.reduce((sum, row) => sum + (row.counts[s.id] || 0), 0);
                        return <td key={s.id} className="af-td af-td-count af-td-total-val">{colTotal}</td>;
                      })}
                    </tr>
                    {dailyData.map(row => (
                      <tr key={row.date} className="af-tr">
                        <td className="af-td af-td-date">{dayjs(row.date).format('DD.MM.YYYY')}</td>
                        {visibleStages.map(s => {
                          const n = row.counts[s.id] || 0;
                          return (
                            <td key={s.id} className={`af-td af-td-count${n > 0 ? ' has-data' : ''}`}>
                              {n > 0 ? n : 0}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { PartnerAnalyticsSection };
